import { currentSessionId, restartExpiredSession, SESSION_HEADER } from "./sessionHeader";

/**
 * Bearer fetch for same-origin `/api/...` calls (routing: docs/operations/runtime-configuration.md).
 * Reference: insights-home src/infrastructure/api/http.ts.
 */

export type TokenGetter = () => Promise<string>;

/** Carries the HTTP status so callers can branch on it (e.g. 503 vs 401). */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/** True for an ApiError, optionally with the given status. */
export function isApiError(error: unknown, status?: number): error is ApiError {
  return error instanceof ApiError && (status === undefined || error.status === status);
}

/** Message from an ASP.NET ProblemDetails body (`detail`, then `title`), else the status line. */
async function errorMessage(response: Response): Promise<string> {
  const fallback = `HTTP ${response.status}: ${response.statusText}`;
  try {
    const body = (await response.json()) as { detail?: string; title?: string };
    return body.detail || body.title || fallback;
  } catch {
    return fallback;
  }
}

async function sendWithAuth(
  url: string,
  options: RequestInit,
  getToken: TokenGetter,
  sessionId: string | null
) {
  const token = await getToken();
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(sessionId ? { [SESSION_HEADER]: sessionId } : {}),
    },
  });
}

/** `withoutSession` sends no session id and never restarts one (the session start). */
export async function fetchWithAuth<T>(
  url: string,
  options: RequestInit,
  getToken: TokenGetter,
  { withoutSession = false }: { withoutSession?: boolean } = {}
): Promise<T> {
  const sentId = withoutSession ? null : await currentSessionId();
  let response = await sendWithAuth(url, options, getToken, sentId);

  if (response.status === 401 && sentId !== null && (await restartExpiredSession(sentId))) {
    response = await sendWithAuth(url, options, getToken, await currentSessionId());
  }

  if (!response.ok) {
    throw new ApiError(await errorMessage(response), response.status);
  }

  const rawBody = await response.text();
  if (!rawBody.trim()) return undefined as T;
  try {
    return JSON.parse(rawBody) as T;
  } catch {
    // Usually the SPA's index.html: /api isn't routed to customer-portal-api.
    throw new ApiError(`Expected JSON from ${url}; is /api routed to the API?`, response.status);
  }
}
